/**
 * Computes effective bounds and clamped selection for an object filter property,
 * respecting the collection-level filter as a ceiling.
 *
 * The effective range is the intersection of:
 * - The object's own data bounds (min/max)
 * - The collection-level applied filter selection (if any)
 *
 * If the object's saved selection falls outside the effective bounds, it is
 * clamped. If the selection becomes invalid (min > max after clamping), it
 * resets to the full effective range.
 *
 * @param {object} objectFilter - { id, label, unit, conversionFactor, min, max, selectedMin, selectedMax }
 * @param {object|null} collectionFilter - Matching collection filter entry, or null/undefined
 * @returns {{ id, label, unit, conversionFactor, min, max, selectedMin, selectedMax, clamped, disabled }}
 */
export const computeEffectiveFilter = (objectFilter, collectionFilter) => {
	// Collection-level applied selection (null = no limit = use object's own bound)
	const collMin = collectionFilter?.selectedMin ?? objectFilter.min;
	const collMax = collectionFilter?.selectedMax ?? objectFilter.max;

	// Effective bounds: intersection of object range and collection selection
	const effectiveMin = Math.max(objectFilter.min, collMin);
	const effectiveMax = Math.min(objectFilter.max, collMax);

	// If bounds collapse — collection filter excludes this object's entire range
	if (effectiveMin >= effectiveMax) {
		return {
			id: objectFilter.id,
			label: objectFilter.label,
			unit: objectFilter.unit,
			conversionFactor: objectFilter.conversionFactor,
			min: effectiveMin,
			max: effectiveMin,
			selectedMin: null,
			selectedMax: null,
			clamped: false,
			disabled: true,
		};
	}

	// Determine raw saved selection (null = no limit = effective bound)
	const rawSelectedMin = objectFilter.selectedMin ?? effectiveMin;
	const rawSelectedMax = objectFilter.selectedMax ?? effectiveMax;

	// Clamp selection to effective bounds
	let clampedMin = Math.max(rawSelectedMin, effectiveMin);
	let clampedMax = Math.min(rawSelectedMax, effectiveMax);

	// Track whether clamping occurred
	const wasClamped = clampedMin !== rawSelectedMin || clampedMax !== rawSelectedMax;

	// If clamping made the selection invalid, reset to full effective range
	if (clampedMin > clampedMax) {
		return {
			id: objectFilter.id,
			label: objectFilter.label,
			unit: objectFilter.unit,
			conversionFactor: objectFilter.conversionFactor,
			min: effectiveMin,
			max: effectiveMax,
			selectedMin: null,
			selectedMax: null,
			clamped: true,
			disabled: false,
		};
	}

	// Normalize: if selection equals the bound, store as null (= "no limit")
	const selectedMin = clampedMin === effectiveMin ? null : clampedMin;
	const selectedMax = clampedMax === effectiveMax ? null : clampedMax;

	return {
		id: objectFilter.id,
		label: objectFilter.label,
		unit: objectFilter.unit,
		conversionFactor: objectFilter.conversionFactor,
		min: effectiveMin,
		max: effectiveMax,
		selectedMin,
		selectedMax,
		clamped: wasClamped,
		disabled: false,
	};
};

/**
 * Computes effective filters for all properties of an object, given the
 * collection-level applied filters.
 *
 * @param {Array} objectFilters - The object's filter property list
 * @param {Array} collectionFilters - The collection-level filter list (applied values)
 * @returns {Array} - Effective filter configs with clamped/disabled flags
 */
export const computeEffectiveFilters = (objectFilters, collectionFilters) => {
	if (!objectFilters?.length) return [];

	const collectionMap = {};
	if (collectionFilters?.length) {
		for (const cf of collectionFilters) {
			collectionMap[cf.id] = cf;
		}
	}

	return objectFilters.map(objFilter =>
		computeEffectiveFilter(objFilter, collectionMap[objFilter.id] ?? null)
	);
};
